import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';

import { ApiService } from '@common';
import { AuthService } from '@common/services/auth.service';
import { environment } from '@environment';

import { buildHandoffAction, isValidHandoffRequestId, submitHandoffForm } from './connect-claude-handoff';

/** The screen the page shows: the consent question, a busy message after a choice, or a failure message. */
export type ConnectClaudeState = 'consent' | 'connecting' | 'cancelling' | 'failed';

/**
 * Completes the sign-in step of connecting Claude to FBO. AuthService sends the browser here; the auth guard has
 * already signed the user in. The page asks the user to allow Claude, then posts their FBO token back to AuthService,
 * or tells AuthService they cancelled so Claude stops waiting.
 */
@Component({
  selector: 'app-connect-claude',
  templateUrl: './connect-claude.component.html',
  styleUrls: ['./connect-claude.component.less'],
  standalone: false
})
export class ConnectClaudeComponent implements OnInit {
  state: ConnectClaudeState = 'consent';

  /**
   * The FBO login being connected. AuthService picks the profile from this login's tenant on its side, so the page
   * names the login rather than a profile or organisation it cannot be sure of.
   */
  signedInAs = '';

  private requestId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    // Reached: the page saved for after sign-in has done its job and must not redirect a later sign-in.
    this.authService.clearRedirectAfterLogin();

    const requestId = this.route.snapshot.queryParamMap.get('request');
    if (!isValidHandoffRequestId(requestId)) {
      this.state = 'failed';
      return;
    }
    this.requestId = requestId;
    this.signedInAs = this.authService.currentUser?.UserName ?? '';
  }

  allow(): void {
    if (this.state !== 'consent') {
      return;
    }
    this.state = 'connecting';

    // AuthService rejects an expired FBO token, so refresh it first if it is about to expire. A refresh that finds
    // the session gone sends the browser to sign in, so save this page again to come back to the request after it.
    this.authService.setRedirectAfterLogin(this.router.url);
    this.apiService
      .refreshToken()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.authService.clearRedirectAfterLogin();
          this.submitAllow();
        },
        error: () => {
          this.authService.clearRedirectAfterLogin();
          this.state = 'failed';
        }
      });
  }

  cancel(): void {
    if (this.state !== 'consent') {
      return;
    }
    this.state = 'cancelling';
    submitHandoffForm(this.document, this.handoffAction(), this.requestId, 'deny');
  }

  private submitAllow(): void {
    const fboToken = this.apiService.getAuthToken();
    if (!fboToken) {
      this.state = 'failed';
      return;
    }

    submitHandoffForm(this.document, this.handoffAction(), this.requestId, 'allow', fboToken);
  }

  private handoffAction(): string {
    return buildHandoffAction(environment.authServiceApiEndpoint);
  }
}
